import type { Identifier } from "ra-core";

import type {
  MetaAdLookupResponse,
  MetaAttribution,
  MetaCampaignReportRow,
  MetaCapiEventFilters,
  MetaCapiEventSummary,
  MetaCapiLogEntry,
  MetaPage,
  MetaPagedResult,
  MetaReportParams,
  SetCtwaClidResult,
} from "../../types";
import { mexicoDayBounds } from "../commons/metaDates";
import { getSupabaseClient } from "./supabase";

/**
 * Almikia Epic EM (TASK-067): thin typed wrappers over the Meta origin / report / Conversions
 * API surface (TASK-064/065/066). Every function only forwards: the RLS policies, the SECURITY
 * DEFINER RPCs and the edge functions are the authority and raise Spanish messages that are
 * surfaced as-is.
 */
const DEFAULT_PER_PAGE = 25;

const fail = (name: string, error: { message?: string } | null): never => {
  console.error(`${name}.error`, error);
  throw new Error(error?.message || "No se pudo completar la acción");
};

const withId = (row: Omit<MetaAttribution, "id">): MetaAttribution => ({
  ...row,
  id: row.deal_id,
});

const range = ({ page = 1, perPage = DEFAULT_PER_PAGE }: MetaPage) => ({
  from: (page - 1) * perPage,
  to: page * perPage - 1,
});

const invokeEdge = async <T>(name: string, body: Record<string, unknown>) => {
  const { data, error } = await getSupabaseClient().functions.invoke<T>(name, {
    body,
  });
  if (error) {
    return fail(name, error);
  }
  return data as T;
};

export const createSupabaseMetaMethods = () => ({
  /** The Meta origin of a lead, or `null` when it has none (RLS = who can read the lead). */
  async getMetaAttribution(
    dealId: Identifier,
  ): Promise<MetaAttribution | null> {
    const { data, error } = await getSupabaseClient()
      .from("deal_meta_attribution")
      .select("*")
      .eq("deal_id", dealId)
      .maybeSingle();
    if (error) {
      return fail("deal_meta_attribution", error);
    }
    return data ? withId(data as Omit<MetaAttribution, "id">) : null;
  },

  /** `set_deal_ctwa_clid()`: direccion / lead_manager record a click-to-WhatsApp id. */
  async setDealCtwaClid(
    dealId: Identifier,
    ctwaClid: string,
  ): Promise<SetCtwaClidResult> {
    const { data, error } = await getSupabaseClient().rpc(
      "set_deal_ctwa_clid",
      {
        p_deal_id: dealId,
        p_ctwa_clid: ctwaClid,
      },
    );
    if (error || !data) {
      return fail("set_deal_ctwa_clid", error);
    }
    return data as SetCtwaClidResult;
  },

  /** `meta_ad_lookup` `{deal_id}`: re-looks up one lead's ad; returns the refreshed rows. */
  lookupMetaAd(dealId: Identifier): Promise<MetaAdLookupResponse> {
    return invokeEdge<MetaAdLookupResponse>("meta_ad_lookup", {
      deal_id: Number(dealId),
    });
  },

  /** `meta_ad_lookup` `{limit}`: re-looks up a bounded batch of pending leads. */
  lookupPendingMetaAds(limit?: number): Promise<MetaAdLookupResponse> {
    return invokeEdge<MetaAdLookupResponse>(
      "meta_ad_lookup",
      limit == null ? {} : { limit },
    );
  },

  /** `meta_campaign_report`: a single RPC (direccion / lead_manager; RLS scopes the rows). */
  async getMetaCampaignReport({
    from,
    to,
    groupBy = "campaign",
  }: MetaReportParams): Promise<MetaCampaignReportRow[]> {
    const { data, error } = await getSupabaseClient().rpc(
      "meta_campaign_report",
      { p_from: from, p_to: to, p_group_by: groupBy },
    );
    if (error) {
      return fail("meta_campaign_report", error);
    }
    return (data ?? []) as MetaCampaignReportRow[];
  },

  /** Outbox list (via `meta_capi_events_summary`), newest first, filtered and paginated. */
  async getMetaCapiEvents(
    filters: MetaCapiEventFilters = {},
  ): Promise<MetaPagedResult<MetaCapiEventSummary>> {
    const { start, end } = mexicoDayBounds(filters.from, filters.to);
    let query = getSupabaseClient()
      .from("meta_capi_events_summary")
      .select("*", { count: "exact" })
      .order("event_time", { ascending: false })
      .order("id", { ascending: false });
    if (filters.status) query = query.eq("status", filters.status);
    if (filters.eventName) query = query.eq("event_name", filters.eventName);
    if (start) query = query.gte("event_time", start);
    if (end) query = query.lte("event_time", end);
    const { from, to } = range(filters);
    const { data, error, count } = await query.range(from, to);
    if (error) {
      return fail("meta_capi_events_summary", error);
    }
    return {
      data: (data ?? []) as MetaCapiEventSummary[],
      total: count ?? 0,
    };
  },

  /** Attempt log of one outbox event, newest first, paginated (direccion only, by RLS). */
  async getMetaCapiLog(
    outboxId: Identifier,
    page: MetaPage = {},
  ): Promise<MetaPagedResult<MetaCapiLogEntry>> {
    const { from, to } = range(page);
    const { data, error, count } = await getSupabaseClient()
      .from("meta_capi_log")
      .select("*", { count: "exact" })
      .eq("outbox_id", outboxId)
      .order("id", { ascending: false })
      .range(from, to);
    if (error) {
      return fail("meta_capi_log", error);
    }
    return { data: (data ?? []) as MetaCapiLogEntry[], total: count ?? 0 };
  },

  /** `meta_capi` `{retry_outbox_id}`: re-queues and re-sends one failed / abandoned event. */
  async retryMetaCapiEvent(outboxId: Identifier): Promise<void> {
    await invokeEdge("meta_capi", { retry_outbox_id: Number(outboxId) });
  },

  /** `meta_capi` `{limit?}`: sends the due pending events. */
  async processPendingMetaCapi(limit?: number): Promise<void> {
    await invokeEdge("meta_capi", limit == null ? {} : { limit });
  },
});
